import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AdminPage, SaveBar } from '../../../components/design-system/admin';
import { TranslatableInput } from '../../../common/components/TranslatableInput';
import { useProductTypesApi } from '../../../common/hooks/use-product-types-api.hook';
import { useResolveTranslation } from '../../../common/hooks/use-resolve-translation.hook';
import {
  addProductType,
  updateProductType as updateProductTypeAction,
} from '../../../redux/productTypes/productTypesActions';
import {
  ProductTypePattern,
  CreateProductTypePayload,
} from '../../../common/models/product-type.model';
import { resolveTranslation, TranslatableString } from '../../../common/utils/translatable-string';
import {
  EditorSection,
  EditorHeader,
  LangRowSpacer,
  ToggleField,
  generateSlug,
} from '../../../modules/superadmin/catalog-config';

// =============================================================================
// Edit Product Type Page
// =============================================================================

const ALL_APPLICATION_TYPES = ['floor', 'walls'];

const K = 'ADMIN_CATALOG.TYPE_EDITOR';

interface FormData {
  name: TranslatableString;
  slug: string;
  applicationTypes: string[];
  patterns: ProductTypePattern[];
  order: number;
  active: boolean;
  isSystem: boolean;
}

const defaultFormData: FormData = {
  name: '',
  slug: '',
  applicationTypes: [],
  patterns: [],
  order: 0,
  active: true,
  isSystem: false,
};

export const EditProductType = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rt } = useResolveTranslation();
  const { productTypeId } = useParams<{ productTypeId: string }>();
  const productTypesApi = useProductTypesApi();

  const isEditing = !!productTypeId;

  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [originalData, setOriginalData] = useState<FormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newPatternName, setNewPatternName] = useState('');
  const [newPatternLabel, setNewPatternLabel] = useState<TranslatableString>('');

  useEffect(() => {
    if (productTypeId) {
      loadProductType(productTypeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId]);

  useEffect(() => {
    setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
  }, [formData, originalData]);

  const loadProductType = async (id: string) => {
    setIsLoading(true);
    try {
      const pt = await productTypesApi.findOneProductType(id);
      if (pt) {
        const data: FormData = {
          name: pt.name,
          slug: pt.slug,
          applicationTypes: pt.applicationTypes || [],
          patterns: pt.patterns || [],
          order: pt.order ?? 0,
          active: pt.active ?? true,
          isSystem: pt.isSystem ?? false,
        };
        setFormData(data);
        setOriginalData(data);
      }
    } catch (error) {
      console.error('Error loading product type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameChange = (name: TranslatableString) => {
    const updates: Partial<FormData> = { name };
    if (!isEditing || !formData.isSystem) {
      const nameStr = typeof name === 'string' ? name : resolveTranslation(name, 'en');
      updates.slug = generateSlug(nameStr);
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleToggleApplicationType = (appType: string) => {
    setFormData(prev => ({
      ...prev,
      applicationTypes: prev.applicationTypes.includes(appType)
        ? prev.applicationTypes.filter(t => t !== appType)
        : [...prev.applicationTypes, appType],
    }));
  };

  const handleAddPattern = () => {
    const labelStr = resolveTranslation(newPatternLabel, 'en');
    if (!newPatternName.trim() || !labelStr.trim()) return;
    if (formData.patterns.some(p => p.name === newPatternName.trim())) return;

    setFormData(prev => ({
      ...prev,
      patterns: [...prev.patterns, { name: newPatternName.trim(), label: newPatternLabel }],
    }));
    setNewPatternName('');
    setNewPatternLabel('');
  };

  const handleRemovePattern = (patternName: string) => {
    setFormData(prev => ({
      ...prev,
      patterns: prev.patterns.filter(p => p.name !== patternName),
    }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      if (isEditing && productTypeId) {
        const updated = await productTypesApi.updateProductType(productTypeId, formData);
        dispatch(updateProductTypeAction(updated));
        setOriginalData(formData);
        toast.success(t(`${K}.SAVED`));
      } else {
        const created = await productTypesApi.createProductType(formData as CreateProductTypePayload);
        dispatch(addProductType(created));
        toast.success(t(`${K}.CREATED`));
        navigate(`/product-types/${created._id}/edit`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving product type:', error);
      toast.error(t('ADMIN_CATALOG.EDITOR.SAVE_FAILED'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    setFormData(originalData);
  };

  const parents = [{ label: t('ADMIN_SHELL.NAV.TYPES'), href: '/product-types' }];

  if (isLoading) {
    return (
      <AdminPage
        title={t('ADMIN_SHELL.COMMON.LOADING')}
        parents={parents}
        className="mx-auto w-full max-w-4xl"
        header={<Skeleton className="h-8 w-56" />}
      >
        <div className="flex justify-center py-16" data-testid="type-editor-loading">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      </AdminPage>
    );
  }

  const title = isEditing ? rt(formData.name) || t(`${K}.EDIT_TITLE`) : t(`${K}.NEW_TITLE`);
  const duplicatePattern = !!newPatternName.trim() && formData.patterns.some(p => p.name === newPatternName.trim());

  return (
    <AdminPage
      title={title}
      parents={parents}
      className="mx-auto w-full max-w-4xl"
      header={
        <EditorHeader title={title} description={t(`${K}.DESCRIPTION`)} onBack={() => navigate('/product-types')} backLabel={t(`${K}.BACK`)} />
      }
    >
      {/* General Information */}
      <EditorSection title={t(`${K}.GENERAL.TITLE`)} testId="type-general">
        <div className="flex flex-col gap-5">
          {/* Name */}
          <TranslatableInput
            value={formData.name}
            onChange={val => handleNameChange(val)}
            label={t(`${K}.GENERAL.NAME`)}
            required
            placeholder={t(`${K}.GENERAL.NAME_PLACEHOLDER`)}
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-[1fr_12rem]">
            {/* Slug */}
            <div>
              <Label htmlFor="product-type-slug" className="mb-1.5 block text-xs text-muted-foreground">
                {t(`${K}.GENERAL.SLUG`)}
              </Label>
              <Input
                id="product-type-slug"
                className="font-mono"
                value={formData.slug}
                onChange={e => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                disabled={isEditing && formData.isSystem}
                placeholder={t(`${K}.GENERAL.SLUG_PLACEHOLDER`)}
                aria-describedby="product-type-slug-hint"
              />
              <p id="product-type-slug-hint" className="mt-1 text-xs text-muted-foreground">
                {isEditing && formData.isSystem ? t(`${K}.GENERAL.SLUG_LOCKED`) : t(`${K}.GENERAL.SLUG_HINT`)}
              </p>
            </div>

            {/* Order */}
            <div>
              <Label htmlFor="product-type-order" className="mb-1.5 block text-xs text-muted-foreground">
                {t(`${K}.GENERAL.ORDER`)}
              </Label>
              <Input
                id="product-type-order"
                type="number"
                className="tabular-nums"
                value={formData.order}
                onChange={e => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          {/* Active toggle */}
          <ToggleField
            id="product-type-active"
            className="border-t pb-0"
            label={t(`${K}.GENERAL.ACTIVE`)}
            description={t(`${K}.GENERAL.ACTIVE_HINT`)}
            checked={formData.active}
            onCheckedChange={checked => setFormData(prev => ({ ...prev, active: checked }))}
          />
        </div>
      </EditorSection>

      {/* Application Types */}
      <EditorSection
        title={t(`${K}.APPLICATIONS.TITLE`)}
        description={t(`${K}.APPLICATIONS.DESCRIPTION`)}
        badge={t(`${K}.APPLICATIONS.SELECTED`, { count: formData.applicationTypes.length })}
        testId="type-applications"
      >
        <div className="flex flex-wrap gap-2">
          {ALL_APPLICATION_TYPES.map(appType => {
            const isSelected = formData.applicationTypes.includes(appType);
            return (
              <button
                key={appType}
                type="button"
                aria-pressed={isSelected}
                onClick={() => handleToggleApplicationType(appType)}
                className={cn(
                  'flex min-w-28 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors',
                  isSelected
                    ? 'border-foreground font-medium text-foreground ring-[0.5px] ring-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'grid size-4 place-items-center rounded-full border',
                    isSelected ? 'border-foreground bg-foreground text-background' : 'border-border',
                  )}
                >
                  {isSelected && <Check className="size-3" />}
                </span>
                {t(`ADMIN_CATALOG.APPLICATION_TYPES.${appType.toUpperCase()}`, {
                  defaultValue: appType.charAt(0).toUpperCase() + appType.slice(1),
                })}
              </button>
            );
          })}
        </div>
      </EditorSection>

      {/* Patterns */}
      <EditorSection
        title={t(`${K}.PATTERNS.TITLE`)}
        description={t(`${K}.PATTERNS.DESCRIPTION`)}
        badge={t(`${K}.PATTERNS.COUNT`, { count: formData.patterns.length })}
        flush
        testId="type-patterns"
      >
        {/* Existing patterns */}
        {formData.patterns.length > 0 && (
          <div className="border-t">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10 ps-4 text-end">#</TableHead>
                  <TableHead className="text-start">{t(`${K}.PATTERNS.COL_PATTERN`)}</TableHead>
                  <TableHead className="text-start">{t(`${K}.PATTERNS.COL_NAME`)}</TableHead>
                  <TableHead className="w-12 pe-4">
                    <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formData.patterns.map((pattern, index) => (
                  <TableRow key={pattern.name} data-testid="type-pattern-row">
                    <TableCell className="ps-4 text-end text-muted-foreground tabular-nums">{index + 1}</TableCell>
                    <TableCell className="text-sm font-medium">{rt(pattern.label)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{pattern.name}</TableCell>
                    <TableCell className="pe-4 text-end">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t(`${K}.PATTERNS.REMOVE`)}
                        className="text-muted-foreground hover:bg-destructive/5 hover:text-destructive"
                        onClick={() => handleRemovePattern(pattern.name)}
                      >
                        <Trash2 />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add new pattern */}
        <div className="border-t p-4">
          <p className="mb-3 text-xs font-medium text-muted-foreground">{t(`${K}.PATTERNS.ADD_TITLE`)}</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-40 flex-1">
              <Label
                htmlFor="product-type-new-pattern-name"
                className="mb-1 block text-xs text-muted-foreground"
              >
                {t(`${K}.PATTERNS.NAME`)}
              </Label>
              <LangRowSpacer />
              <Input
                id="product-type-new-pattern-name"
                className="font-mono"
                value={newPatternName}
                onChange={e => setNewPatternName(e.target.value)}
                placeholder={t(`${K}.PATTERNS.NAME_PLACEHOLDER`)}
                aria-invalid={duplicatePattern || undefined}
                aria-describedby={duplicatePattern ? 'product-type-new-pattern-duplicate' : undefined}
              />
            </div>
            <div className="min-w-40 flex-1">
              <TranslatableInput
                value={newPatternLabel}
                onChange={val => setNewPatternLabel(val)}
                label={t(`${K}.PATTERNS.LABEL`)}
                placeholder={t(`${K}.PATTERNS.LABEL_PLACEHOLDER`)}
              />
            </div>
            <Button
              variant="outline"
              onClick={handleAddPattern}
              disabled={!newPatternName.trim() || !resolveTranslation(newPatternLabel, 'en').trim()}
            >
              <Plus />
              {t(`${K}.PATTERNS.ADD`)}
            </Button>
          </div>
          {duplicatePattern && (
            <p id="product-type-new-pattern-duplicate" className="mt-2 text-xs text-destructive">
              {t(`${K}.PATTERNS.DUPLICATE`)}
            </p>
          )}
        </div>
      </EditorSection>

      <SaveBar
        dirty={hasChanges}
        saving={isSubmitting}
        disabled={!resolveTranslation(formData.name, 'en')}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saveLabel={isEditing ? t('ADMIN_SHELL.COMMON.SAVE') : t(`${K}.CREATE`)}
        testId="editor-save"
      />
    </AdminPage>
  );
};

export default EditProductType;
